import React, { useEffect, useState } from 'react';
import EmptyContent from './content/EmptyContent';
import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { addLayer, allSteps, getMainConfig, selectLayers, updateStep } from '../../../../../redux/stepsSlice';
import { getActiveStep, removeActiveLayer, setActiveStep } from '../../../../../redux/toolsSlice';
import Renderer from './Renderer';
import { DateIcon } from '../../../../assets/Icons';
import ButtonGroup from './content/fields/ButtonGroup';
import moment from 'moment';
import LargeTextContent, { LargeTextLayerContentDefaults } from './content/fields/LargeTextContent';
import PreviewHeader from '../../PreviewHeader';
import { getMaterial } from '../../../../../redux/materialSlice';
import { getValue } from '../../../utils/tools';

// BL-081 — a step carries a live display rule when it has at least one complete
// condition. Used to show the "Rule" badge in the step list.
const stepHasRule = (step) =>
  step?.display_rule &&
  Array.isArray(step.display_rule.conditions) &&
  step.display_rule.conditions.some((c) => c && c.attribute);

const Editor = ({ previewDevice = 'desktop' }) => {
  const dispatch = useDispatch();
  const activeStep = useSelector((state) => getActiveStep(state));
  const steps = useSelector((state) => allSteps(state));
  const material = useSelector((state) => getMaterial(state));
  const mainConfig = useSelector((state) => getMainConfig(state));
  const stepId = activeStep.id;
  const layers = useSelector((state) => selectLayers(state, activeStep.id));
  const mainLayer = layers.find((entry) => entry.id === 'main') || layers.find((entry) => entry.type === 'large_text');
  const contentLayers = layers.filter((entry) => entry.id !== mainLayer?.id);
  const canRender = contentLayers.length > 0;
  const activeIndex = steps.findIndex((step) => step.id === activeStep?.id);
  const config = Array.isArray(mainConfig) && mainConfig.length > 0 ? mainConfig : material?.config;

  const defaultColors = {
    background: '#F6F8F9',
    text: '#363841',
    config
  };

  const caHeaderColor = getValue(defaultColors, 'ca', 'caHeaderColor');
  const caHeaderFontSize = getValue(defaultColors, 'ca', 'caHeaderFontSize');
  const caTextColor = getValue(defaultColors, 'ca', 'caTextColor');
  const contentAreaStyle = {
    '--path-content-header-bg': caHeaderColor || '#F3F3F3',
    '--path-content-header-font-size': caHeaderFontSize || '18px',
    '--path-content-header-text': caTextColor || '#363841'
  };

  const [dueDate, setDueDate] = useState('');
  const [isStepBodyOpen, setIsStepBodyOpen] = useState(true);

  useEffect(() => {
    if (activeStep && activeStep.dueDate) {
      setDueDate(activeStep.dueDate);
    } else {
      setDueDate('');
    }

    if (stepId && layers && layers.length === 0) {
      dispatch(
        addLayer({
          stepId,
          layer: {
            id: 'main',
            type: 'large_text',
            fontSize: 34,
            color: '#000000',
            fontFamily: 'Open Sans',
            content: 'This is large title'
          }
        })
      );
    }
  }, [activeStep]);

  useEffect(() => {
    setIsStepBodyOpen(true);
  }, [activeStep?.id]);

  useEffect(() => {
    const onSelect = (dateText) => {
      const dueDateValue = dateText.length > 0 ? dateText[0] : '';
      setDueDate(dueDateValue);
      const updatedStep = {
        ...activeStep,
        dueDate: dueDateValue ? dueDateValue.toISOString() : ''
      };
      dispatch(updateStep(updatedStep));
    };

    $('.un-path-step-calendar').datepick({
      onSelect,
      dateFormat: 'dd/mm/yyyy',
      minDate: new Date(),
      popupContainer: 'body'
    });

    return () => {
      $('.un-path-step-calendar').datepick('destroy');
    };
  }, [activeStep, dispatch]);

  const [{ canDrop, isOver }, drop] = useDrop(() => ({
    accept: 'card',
    drop: () => ({ name: 'Editor' }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }));

  const isActiveDrop = canDrop && isOver;
  let backgroundColor = '#CBD0D7';

  if (isActiveDrop) {
    backgroundColor = 'darkgreen';
  } else if (canDrop) {
    backgroundColor = 'darkkhaki';
  }

  const handlePreviewStepChange = (step) => {
    if (step.id === activeStep?.id) {
      setIsStepBodyOpen((prevState) => !prevState);
      if (isStepBodyOpen) {
        dispatch(removeActiveLayer());
      }
      return;
    }

    dispatch(setActiveStep(step));
    dispatch(removeActiveLayer());
    setIsStepBodyOpen(true);
  };

  return (
    <div className={`un-path-preview-stage un-path-preview-stage--${previewDevice}`}>
      <div className={`un-path-editor-preview un-path-editor-preview--${previewDevice}`} style={contentAreaStyle}>
        <PreviewHeader />

        <div className="un-path-editor-preview__stack">
          {steps.map((step, index) => {
            const isActive = step.id === activeStep?.id;
            const isExpanded = isActive && isStepBodyOpen;
            const stateLabel = isActive ? 'Currently active' : index < activeIndex ? 'Completed' : 'Pending';

            return (
              <article
                key={step.id}
                className={`un-path-editor-step ${isExpanded ? 'is-active' : 'is-collapsed'} ${index < activeIndex ? 'is-complete' : ''}`}
              >
                <button type="button" className="un-path-editor-step__summary" onClick={() => handlePreviewStepChange(step)}>
                  <div className="un-path-editor-step__summary-left">
                    <span className="un-path-editor-step__number">{index + 1}</span>
                    <div className="un-path-editor-step__summary-copy">
                      <span className="un-path-editor-step__summary-title">
                        {step.title || step.name || `Step ${index + 1}`}
                        {stepHasRule(step) && (
                          <span className="un-rule-badge" title="This step has a display rule">
                            <svg width="9" height="9" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                              <path d="M3 4h14M6 10h8M9 16h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            Rule
                          </span>
                        )}
                      </span>
                      <span className="un-path-editor-step__summary-status">{stateLabel}</span>
                    </div>
                  </div>
                  <span className="un-path-editor-step__summary-indicator" aria-hidden="true">
                    {index < activeIndex ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="10" fill="currentColor" />
                        <path d="M8.529 12.974 5.95 10.395a.75.75 0 0 1 1.06-1.06l1.519 1.519 4.458-4.458a.75.75 0 1 1 1.06 1.06l-5.518 5.518Z" fill="#fff" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                        <circle cx="11" cy="11" r="10" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    )}
                  </span>
                </button>

                {isExpanded && (
                  <div className="un-path-editor-step__body">
                    <div className="un-path-editor-step__hero">
                      <div className="element-wrapper element-wrapper--hero">
                        {mainLayer ? (
                          <LargeTextContent layer={mainLayer} />
                        ) : (
                          <h2 className="path-step-content__fallback-title">{activeStep?.title || activeStep?.name || 'Current step'}</h2>
                        )}
                      </div>
                    </div>

                    <div className="un-path-editor-step__meta-row">
                      <div className="flex-design-form un-path-editor-step__meta-item">
                        <DateIcon />
                        <input
                          style={{ width: 'auto' }}
                          type="text"
                          className="input-design-form calendar un-path-step-calendar"
                          placeholder="Set Due Date"
                          value={dueDate ? moment(dueDate).format('DD/MM/YYYY') : ''}
                        />
                      </div>
                    </div>

                    {activeStep?.description && (
                      <p className="un-path-editor-step__description">{activeStep.description}</p>
                    )}

                    <div className="un-path-editor-step__canvas" ref={drop}>
                      {canRender ? <Renderer layers={contentLayers} /> : <EmptyContent backgroundColor={backgroundColor} />}
                    </div>

                    <div className="element-wrapper un-path-editor-step__actions">
                      <ButtonGroup />
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Editor;
